
import React, { useState, useEffect, useRef } from 'react';
import { Search, X, Loader2, Sparkles, Zap } from 'lucide-react';
import { POPULAR_ITEMS } from '../constants';
import { RentalItem } from '../types';

interface SearchBarProps {
  onSearch: (query: string) => void;
}

const SearchBar: React.FC<SearchBarProps> = ({ onSearch }) => {
  const [input, setInput] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isAiPro, setIsAiPro] = useState(false);
  const [suggestions, setSuggestions] = useState<RentalItem[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkKey = () => {
      const key = process.env.API_KEY;
      setIsAiPro(!!(key && key !== 'undefined' && key.trim().length > 5));
    };
    checkKey();
    const interval = setInterval(checkKey, 2000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (input.trim() === '') {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    const timeoutId = setTimeout(() => {
      const filtered = POPULAR_ITEMS.filter(item => 
        item.name.toLowerCase().includes(input.toLowerCase())
      ).slice(0, 5);
      setSuggestions(filtered);
      setIsLoading(false);
    }, 150);

    return () => clearTimeout(timeoutId);
  }, [input]);

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (input.trim()) {
      onSearch(input);
      setIsFocused(false);
    } else {
      setIsLoading(true);
      setTimeout(() => {
        const random = POPULAR_ITEMS[Math.floor(Math.random() * POPULAR_ITEMS.length)];
        onSearch(random.name);
        setIsLoading(false);
      }, 500);
    }
  };

  const handleSuggestionClick = (name: string) => {
    setInput(name);
    onSearch(name);
    setIsFocused(false);
  };

  const handleImFeelingLucky = () => {
    const random = POPULAR_ITEMS[Math.floor(Math.random() * POPULAR_ITEMS.length)];
    setInput(random.name);
    onSearch(random.name);
  };

  return (
    <div className="w-full max-w-2xl px-2 md:px-4 relative" ref={containerRef}>
      <form 
        onSubmit={handleSubmit}
        className={`flex items-center w-full px-4 md:px-5 py-2.5 md:py-3.5 rounded-full border border-gray-200 transition-all duration-300 
          ${isFocused ? 'shadow-xl border-transparent ring-4 ring-blue-50' : 'shadow-[0_0_15px_rgba(66,133,244,0.08)] hover:shadow-md'} 
          ${isAiPro ? 'shadow-[0_0_25px_rgba(245,158,11,0.15)] border-amber-200' : 'border-blue-50'}
          bg-white relative z-50`} 
      >
        <Search className={`w-4 h-4 md:w-5 h-5 ${isAiPro ? 'text-amber-500' : 'text-blue-500'} mr-3 shrink-0 transition-colors`} />
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onFocus={() => setIsFocused(true)}
          placeholder={isAiPro ? "Vraag Deniz om technisch advies..." : "Zoek in Deniz' slimme magazijn..."}
          className="flex-grow outline-none text-base md:text-lg bg-transparent text-gray-900 placeholder:text-gray-400 min-w-0 font-medium"
          autoComplete="off"
        />
        <div className="flex items-center space-x-2 shrink-0">
          {isAiPro ? (
            <Zap className="w-4 h-4 text-amber-500 fill-amber-500 animate-pulse hidden sm:block" />
          ) : (
            <Sparkles className="w-4 h-4 text-blue-400 animate-pulse hidden sm:block" />
          )}
          {isLoading && <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />}
          {input && !isLoading && (
            <button type="button" onClick={() => setInput('')} className="p-1">
              <X className="w-4 h-4 md:w-5 h-5 text-gray-400 hover:text-gray-700 transition-colors" />
            </button>
          )}
        </div>
      </form>

      {/* Autocomplete Dropdown */}
      {isFocused && (isLoading || (suggestions.length > 0 && input.trim() !== '')) && (
        <div className="absolute top-[50%] left-0 right-0 pt-8 md:pt-10 mx-2 md:mx-4 bg-white border border-gray-200 border-t-0 rounded-b-3xl shadow-2xl z-40 overflow-hidden pb-3 animate-in fade-in slide-in-from-top-2 duration-200">
          {isLoading && input.trim() !== '' ? (
            <div className="px-5 py-6 flex flex-col items-center justify-center space-y-2">
              <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
              <span className="text-[10px] text-gray-400 uppercase tracking-widest font-black">Deniz scant...</span>
            </div>
          ) : (
            suggestions.map((item, idx) => (
              <button
                key={idx}
                className="w-full text-left px-5 py-3 hover:bg-gray-50 flex items-center space-x-3 transition-colors active:bg-blue-50"
                onMouseDown={() => handleSuggestionClick(item.name)}
              >
                <div className="w-6 h-6 bg-gray-100 rounded-md flex items-center justify-center shrink-0">
                   <Search className="w-3 h-3 text-gray-400" />
                </div>
                <div className="flex flex-col min-w-0">
                  <span className="text-gray-900 font-bold text-sm md:text-base truncate">{item.name}</span>
                  <span className="text-[9px] text-blue-500 uppercase font-black tracking-tighter">{item.category}</span>
                </div>
              </button>
            ))
          )}
        </div>
      )}
      
      {/* Search Buttons */}
      <div className="flex flex-col sm:flex-row justify-center mt-6 md:mt-8 space-y-3 sm:space-y-0 sm:space-x-4 px-4">
        <button 
          type="button"
          className={`px-8 py-2.5 rounded-md text-sm transition-all font-bold active:scale-95 shadow-sm border
            ${isAiPro ? 'bg-amber-600 text-white border-amber-600 hover:bg-amber-700' : 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'}`}
          onClick={handleSubmit}
        >
          {isAiPro ? 'Expert Advies Vragen' : 'Vraag het aan Deniz'}
        </button>
        <button 
          type="button"
          className="px-8 py-2.5 bg-[#f8f9fa] border border-[#f8f9fa] rounded-md hover:border-gray-300 hover:shadow-sm text-sm text-gray-700 transition-all font-bold active:scale-95 shadow-sm"
          onClick={handleImFeelingLucky}
        >
          Deniz' Keuze
        </button>
      </div>
    </div>
  );
};

export default SearchBar;
